import { CircleCheck } from 'lucide-react';
import { useId } from 'react';
import { useIntl } from 'react-intl';
import { route } from 'ziggy-js';
import Button from '@/Components/Marketing/Button';
import { ICONS, STAGE_COPY, STAGES } from '@/Components/Marketing/consultingStages';
import { CONSULTATION_URL } from '@/Components/Marketing/links';
import Reveal from '@/Components/Marketing/Reveal';
import Section from '@/Components/Marketing/Section';
import { NightBackdrop } from '@/Components/Marketing/Texture';
import { Heading, Lead } from '@/Components/Marketing/Typography';
import { cross, keyFamily } from '@/lib/keyFamily';
import { cn } from '@/lib/utils';

/** The platform's setup with a team, in order: what they bring, and the release last. */
export const SETUP = Object.freeze(['batteries', 'data', 'suppliers', 'release']);

/** Each setup step's title and text. */
export const HOME_SETUP_COPY = keyFamily('site.home.consulting.setup.{0}.{1}', cross(SETUP, ['title', 'text']));

/**
 * Home, under the logo strip: the consulting division (2026-10-04, in place
 * of the integration section it now holds), as a consultancy that advises a
 * team and prepares it for the rules and their passports (2026-10-05). How
 * we work through the rules (the Consulting page's Inform, Plan, Implement),
 * then how we set a team up on the platform and stay until its first
 * passports are released. A consultation, or the Consulting page.
 *
 * Home's one night band, under the night grid: it took the band over from
 * the pinned regulation story, dropped above it on 2026-10-05.
 */
export default function Consulting() {
    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });
    const headingId = useId();

    return (
        <Section tone="dark" aria-labelledby={headingId} data-band="night" className="relative isolate overflow-hidden">
            <NightBackdrop />
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-end lg:gap-16">
                <Reveal className="lg:col-span-7">
                    <Heading id={headingId} className="max-w-[14em]">{t('site.home.consulting.title')}</Heading>
                    <Lead className="mt-6 max-w-[46ch]">{t('site.home.consulting.lead')}</Lead>
                </Reveal>
                <Reveal delay={0.06} className="flex flex-wrap gap-3 lg:col-span-5 lg:justify-end">
                    <Button href={CONSULTATION_URL} size="lg">{t('site.cta.consultation')}</Button>
                    <Button href={route('outer.consulting')} variant="secondary" size="lg">{t('site.home.consulting.link')}</Button>
                </Reveal>
            </div>

            <Reveal delay={0.06}>
                <ol role="list" data-stages="" className="mt-14 grid grid-cols-1 gap-10 border-t border-white/10 pt-10 sm:mt-16 md:grid-cols-3 md:gap-12">
                    {STAGES.map((stage) => {
                        const Icon = ICONS[stage];

                        return (
                            <li key={stage} data-stage={stage}>
                                <span
                                    aria-hidden="true"
                                    className="flex size-11 items-center justify-center rounded-lg bg-mk-night-2 text-mk-night-orange ring-1 ring-inset ring-white/10"
                                >
                                    <Icon strokeWidth={1.5} className="size-5" />
                                </span>
                                <h3 className="mt-6 text-xl font-semibold tracking-[-0.02em] text-mk-night-text">
                                    {t(STAGE_COPY.key(stage, 'title'))}
                                </h3>
                                <p className="mt-3 max-w-[38ch] text-base leading-relaxed text-pretty text-mk-night-body">
                                    {t(STAGE_COPY.key(stage, 'text'))}
                                </p>
                            </li>
                        );
                    })}
                </ol>
            </Reveal>

            <Reveal delay={0.06}>
                <div data-setup="" className="mt-14 rounded-mk-card bg-mk-night-2 p-6 ring-1 ring-inset ring-white/10 sm:mt-16 sm:p-8">
                    <p className="text-lg font-semibold tracking-[-0.02em] text-mk-night-text">{t('site.home.consulting.setup.title')}</p>
                    <dl className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
                        {SETUP.map((key) => {
                            const release = key === 'release';

                            return (
                                <div key={key} data-setup-step={key}>
                                    <dt className={cn('flex items-center gap-2 text-[15px] font-semibold', release ? 'text-mk-night-orange' : 'text-mk-night-text')}>
                                        {release && <CircleCheck aria-hidden="true" strokeWidth={1.5} className="size-[18px] shrink-0" />}
                                        {t(HOME_SETUP_COPY.key(key, 'title'))}
                                    </dt>
                                    <dd className="mt-2 text-[15px] leading-relaxed text-pretty text-mk-night-body">
                                        {t(HOME_SETUP_COPY.key(key, 'text'))}
                                    </dd>
                                </div>
                            );
                        })}
                    </dl>
                </div>
            </Reveal>
        </Section>
    );
}
